import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarClock, Phone, Search, Scissors, Droplets, X, ArrowLeft, Save, Trash2, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { normalizePhone, isValidGrPhone } from "@/lib/phone";
import { isDateDisabled, getSlotsForDate } from "@/lib/hours";
import { track } from "@/lib/analytics";

export const MyBookingsDialog = ({ open, onOpenChange, initialPhone = "" }) => {
  const { t } = useLang();
  const m = t.my_bookings;
  const [phone, setPhone] = useState(initialPhone);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(null); // {type, booking, form}

  React.useEffect(() => {
    if (open && initialPhone && isValidGrPhone(initialPhone) && !result) {
      setPhone(initialPhone);
      // Auto-lookup after mount
      (async () => {
        setLoading(true);
        try {
          const res = await api.post("/my-bookings", { phone: initialPhone });
          setResult(res.data);
          track("my_bookings_lookup", { source: "email_link", count_g: res.data.grooming.length, count_sw: res.data.selfwash.length });
        } catch {
          // silently ignore, user can retry manually
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open, initialPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setPhone(""); setResult(null); setEditing(null); };
  const close = () => { onOpenChange(false); setTimeout(reset, 300); };

  const lookup = async () => {
    if (!isValidGrPhone(phone)) { toast.error(m.phone_error); return; }
    setLoading(true);
    try {
      const res = await api.post("/my-bookings", { phone });
      setResult(res.data);
      track("my_bookings_lookup", { count_g: res.data.grooming.length, count_sw: res.data.selfwash.length });
    } catch (err) {
      toast.error(err.response?.status === 422 ? m.phone_error : m.lookup_error);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (type, booking) => {
    const dateKey = type === "grooming" ? "preferred_date" : "date";
    const timeKey = type === "grooming" ? "preferred_time" : "time";
    setEditing({
      type, booking,
      form: {
        date: booking[dateKey],
        time: booking[timeKey],
        notes: booking.notes || "",
      },
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const { type, booking, form } = editing;
    setLoading(true);
    try {
      await api.patch(`/my-bookings/${type}/${booking.id}`, {
        phone, date: form.date, time: form.time, notes: form.notes,
      });
      toast.success(m.saved);
      track("my_bookings_edit", { type });
      await lookup();
      setEditing(null);
    } catch (err) {
      const msg = err.response?.status === 409 ? m.slot_taken : err.response?.status === 400 ? m.invalid_time : m.save_error;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (type, bid) => {
    if (!window.confirm(m.confirm_cancel)) return;
    setLoading(true);
    try {
      await api.delete(`/my-bookings/${type}/${bid}?phone=${phone}`);
      toast.success(m.cancelled);
      track("my_bookings_cancel", { type });
      await lookup();
    } catch {
      toast.error(m.cancel_error);
    } finally {
      setLoading(false);
    }
  };

  // Edit view
  if (editing) {
    const { type, booking, form } = editing;
    const dateObj = form.date ? new Date(form.date + "T00:00:00") : null;
    return (
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="bg-brand-cream max-w-md rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="my-edit-dialog">
          <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
                <Pencil className="h-5 w-5" /> {m.edit_title}
              </DialogTitle>
              <DialogDescription className="text-brand-cream/80">
                {type === "grooming" ? m.grooming : m.selfwash} · {booking.customer_name}
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-6 space-y-4">
            <div>
              <Label className="text-brand-ink font-semibold">{m.date}</Label>
              <Input
                type="date"
                value={form.date || ""}
                onChange={(e) => setEditing({ ...editing, form: { ...form, date: e.target.value, time: "" } })}
                min={new Date().toISOString().slice(0, 10)}
                className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11"
                data-testid="my-edit-date"
              />
            </div>
            <EditTimeSelect type={type} form={form} onChange={(v) => setEditing({ ...editing, form: { ...form, time: v } })} dateObj={dateObj} />
            <div>
              <Label className="text-brand-ink font-semibold">{m.notes}</Label>
              <Textarea
                value={form.notes || ""}
                onChange={(e) => setEditing({ ...editing, form: { ...form, notes: e.target.value } })}
                className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand min-h-[80px]"
                data-testid="my-edit-notes"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <Button
                variant="outline"
                onClick={() => setEditing(null)}
                className="rounded-full border-brand/30 text-brand font-bold h-11 px-5"
                data-testid="my-edit-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> {m.back}
              </Button>
              <Button
                onClick={saveEdit}
                disabled={loading}
                className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
                data-testid="my-edit-save-btn"
              >
                <Save className="h-4 w-4 mr-1" /> {loading ? m.saving : m.save}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-brand-cream max-w-lg rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="my-bookings-dialog">
        <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
              <CalendarClock className="h-5 w-5" /> {m.title}
            </DialogTitle>
            <DialogDescription className="text-brand-cream/80">{m.subtitle}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-4">
          {!result && (
            <>
              <div>
                <Label className="text-brand-ink font-semibold flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-brand" /> {m.phone_label}
                </Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(normalizePhone(e.target.value).slice(0, 10))}
                  onKeyDown={(e) => e.key === "Enter" && lookup()}
                  inputMode="tel"
                  maxLength={10}
                  placeholder="69XXXXXXXX"
                  className={`rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11 mt-1 ${phone.length > 0 && !isValidGrPhone(phone) ? "border-rose-400" : ""}`}
                  data-testid="my-phone-input"
                />
                <p className="mt-1 text-xs text-brand-ink/50">{m.phone_hint}</p>
              </div>
              <Button
                onClick={lookup}
                disabled={loading || !isValidGrPhone(phone)}
                className="w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-11"
                data-testid="my-lookup-btn"
              >
                <Search className="h-4 w-4 mr-2" /> {loading ? m.searching : m.search}
              </Button>
            </>
          )}

          {result && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-brand-ink/70"><Phone className="inline h-3.5 w-3.5 mr-1" /> {phone}</p>
                <Button
                  onClick={reset}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-brand text-xs h-8"
                  data-testid="my-back-lookup"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" /> {m.change_phone}
                </Button>
              </div>

              {result.grooming.length === 0 && result.selfwash.length === 0 && (
                <div className="rounded-2xl bg-white border border-brand/10 p-6 text-center" data-testid="my-empty">
                  <AlertTriangle className="h-8 w-8 mx-auto text-brand-ink/40 mb-2" />
                  <p className="text-brand-ink/60">{m.no_bookings}</p>
                </div>
              )}

              {result.grooming.map((b) => (
                <BookingCard key={b.id} type="grooming" booking={b} onEdit={() => startEdit("grooming", b)} onCancel={() => cancelBooking("grooming", b.id)} m={m} />
              ))}
              {result.selfwash.map((b) => (
                <BookingCard key={b.id} type="selfwash" booking={b} onEdit={() => startEdit("selfwash", b)} onCancel={() => cancelBooking("selfwash", b.id)} m={m} />
              ))}
            </>
          )}

          <p className="text-[11px] text-brand-ink/50 text-center italic pt-2">{m.privacy_note}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BookingCard = ({ type, booking, onEdit, onCancel, m }) => {
  const isG = type === "grooming";
  const date = isG ? booking.preferred_date : booking.date;
  const time = isG ? booking.preferred_time : booking.time;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`rounded-2xl border p-4 shadow-sm ${isG ? "bg-brand-soft/50 border-brand/20" : "bg-bamboo-soft/50 border-bamboo/20"}`}
      data-testid={`my-booking-${type}-${booking.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${isG ? "bg-brand text-brand-cream" : "bg-bamboo text-brand-cream"}`}>
            {isG ? <Scissors className="h-3 w-3" /> : <Droplets className="h-3 w-3" />}
            {isG ? m.grooming : m.selfwash}
          </span>
          <h4 className="mt-2 font-heading text-lg font-bold text-brand-ink flex items-center gap-1.5">
            <CalendarClock className="h-4 w-4" /> {date} · {time}
          </h4>
          {isG ? (
            <p className="text-sm text-brand-ink/70 mt-1">🐾 {booking.pet_name} {booking.pet_breed ? `(${booking.pet_breed})` : ""}</p>
          ) : (
            <p className="text-sm text-brand-ink/70 mt-1">🛁 {booking.plan} · €{booking.total_price}</p>
          )}
          {booking.notes && <p className="text-xs text-brand-ink/60 italic mt-1">"{booking.notes}"</p>}
          <p className="text-xs text-brand-ink/50 mt-1">{m.status}: {booking.status}</p>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={onEdit} size="sm" variant="outline" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft font-bold h-9 flex-1" data-testid={`my-edit-${booking.id}`}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> {m.edit}
        </Button>
        <Button onClick={onCancel} size="sm" variant="outline" className="rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 font-bold h-9 flex-1" data-testid={`my-cancel-${booking.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" /> {m.cancel}
        </Button>
      </div>
    </motion.div>
  );
};

// Time select for edit — for grooming, fetch via API; for selfwash use static hours.
const EditTimeSelect = ({ type, form, onChange, dateObj }) => {
  const { t } = useLang();
  const m = t.my_bookings;
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!form.date) { setSlots([]); return; }
    if (type === "grooming") {
      setLoading(true);
      api.get(`/grooming/availability?date=${form.date}`)
        .then((r) => setSlots(r.data.available || []))
        .catch(() => setSlots([]))
        .finally(() => setLoading(false));
    } else if (dateObj) {
      setSlots(getSlotsForDate(dateObj));
    }
  }, [form.date, type, dateObj]);

  const options = form.time && !slots.includes(form.time) ? [form.time, ...slots] : slots;

  return (
    <div>
      <Label className="text-brand-ink font-semibold">{m.time}</Label>
      <Select value={form.time || ""} onValueChange={onChange} disabled={loading || !form.date}>
        <SelectTrigger className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11" data-testid="my-edit-time">
          <SelectValue placeholder={loading ? m.loading : "--:--"} />
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 && <div className="px-3 py-2 text-sm text-brand-ink/50">{m.no_slots}</div>}
          {options.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

export default MyBookingsDialog;
